import React from 'react';
import { RotateCcw, ShieldCheck, Truck, Ban, CreditCard, XCircle, Mail, Globe } from 'lucide-react';

const Bullets = ({ items }) => (
  <ul className="mt-2 space-y-1.5 text-gray-600 text-sm sm:text-base">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ icon: Icon, title, children }) => (
  <div className="border-t border-gray-100 pt-6 first:border-t-0 first:pt-0">
    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
      <Icon size={18} className="text-cyan-600" /> {title}
    </h3>
    <div className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">{children}</div>
  </div>
);

export const ReturnPolicy = () => (
  <section id="return-policy" className="mt-20 scroll-mt-28" data-testid="return-policy-section">
    <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Return &amp; Refund Policy</h2>
        <p className="text-sm text-gray-500">Last Updated: June 2026</p>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
          At Rezvani, we stand behind the quality of our products and are committed to customer satisfaction.
        </p>
      </div>

      <div className="space-y-6">
        <Block icon={RotateCcw} title="30-Day Return Policy">
          <p>Customers may request a return within 30 days of delivery. To be eligible for a return, the product must be:</p>
          <Bullets items={[
            'In its original condition',
            'Free from damage caused by misuse, neglect, improper installation, or unauthorized modifications',
            'Returned with all included accessories, manuals, and components',
          ]} />
        </Block>

        <Block icon={ShieldCheck} title="Damaged or Defective Products">
          <p>If your Rezvani product arrives damaged or defective, please contact us within 7 days of delivery. To process a damage or defect claim, we may request:</p>
          <Bullets items={[
            'Photos of the product',
            'Photos of the packaging',
            'A brief description of the issue',
            'Video documentation when applicable',
          ]} />
          <p className="mt-3">If a manufacturing defect or shipping damage is confirmed, Rezvani will provide replacement parts, a replacement product, repair service, or a full refund at our discretion.</p>
        </Block>

        <Block icon={Truck} title="Return Shipping">
          <p>For approved returns due to manufacturing defects, shipping damage, or products that do not function as intended, Rezvani will cover the cost of return shipping.</p>
          <p className="mt-2">For customer-remorse returns (ordered by mistake, no longer wanted, etc.), the customer is responsible for return shipping costs and any applicable restocking fees.</p>
        </Block>

        <Block icon={Ban} title="Non-Returnable Items">
          <p>The following items are not eligible for return:</p>
          <Bullets items={[
            'Products damaged due to misuse, abuse, neglect, or improper maintenance',
            'Products modified or repaired by unauthorized parties, or failure to follow operating instructions',
            'Clearance, custom, or special-order items unless defective',
          ]} />
        </Block>

        <Block icon={CreditCard} title="Refund Process">
          <p>Once a returned product is received and inspected, we will notify you regarding approval of your refund.</p>
          <p className="mt-2">Approved refunds will be issued to the original payment method within 5–10 business days after inspection and approval.</p>
          <p className="mt-2">Shipping charges are non-refundable unless the return is due to a verified Rezvani error, shipping damage, or product defect.</p>
        </Block>

        <Block icon={XCircle} title="Order Cancellations">
          <p>Orders may be canceled before shipment for a full refund.</p>
          <p className="mt-2">Orders canceled after shipment may be subject to return shipping charges and applicable restocking fees.</p>
        </Block>

        <Block icon={Mail} title="Contact Us">
          <p>For return requests or warranty assistance, please contact:</p>
          <div className="mt-3 rounded-xl bg-gray-50 p-4 space-y-1.5">
            <p className="font-semibold text-gray-900">Rezvani Customer Support</p>
            <p className="flex items-center gap-2">
              <Mail size={15} className="text-cyan-600" />
              <a href="mailto:rezvanifurniturestudio@gmail.com" className="text-cyan-700 hover:underline">rezvanifurniturestudio@gmail.com</a>
            </p>
            <p className="flex items-center gap-2">
              <Globe size={15} className="text-cyan-600" />
              <a href="https://www.rezvanifurniturestudio.com/" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">www.rezvanifurniturestudio.com</a>
            </p>
          </div>
          <p className="mt-4 text-gray-600">
            We are committed to resolving issues quickly and providing a positive ownership experience for every Rezvani customer.
          </p>
        </Block>
      </div>
    </div>
  </section>
);

export default ReturnPolicy;
